import { Bars3Icon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  List,
  ListItem,
  Text,
  useBreakpoint,
  useDisclosure,
} from "@highoutput/ui-components";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import Calendly from "./calendly";
import MegaphoneIcon from "./icons/megaphone";
import PencilIcon from "./icons/pencil";
import ShieldDollarIcon from "./icons/shield-dollar";
import UsersIcon from "./icons/users";
import For from "./utilities/for";

export default function Navbar() {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const router = useRouter();

  const navbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (navbarRef.current) revealOnScrollUp(navbarRef.current);
  }, []);

  return (
    <>
      <Box
        ref={navbarRef}
        top={0}
        bgColor="white"
        zIndex={9}
        position="sticky"
        paddingX={{
          base: "16px",
          lg: "28px",
          xl: "32px",
        }}
        paddingY={{
          base: "16px",
          lg: "28px",
          xl: "32px",
        }}
        borderBottom="1px solid"
        borderColor="rgba(240,237,236,0.4)"
        transition="all 400ms ease-in-out"
        fontFamily="'Inter', sans-serif"
      >
        <Flex maxWidth="1120px" marginX="auto" justifyContent="space-between" alignItems="center">
          <Box
            as={Link}
            role="navigation"
            aria-label="Go to homepage"
            href="/"
            position="relative"
            width={{
              base: "calc(30px + 15px + 29px + 23px)",
              lg: "calc(45.33px + 43.34px + 24.78px + 33.98px)",
            }}
            height={{
              base: "24px",
              lg: "36.03px",
            }}
          >
            <Image
              src="/images/advante-logo.webp"
              alt=""
              width={160}
              height={50}
              draggable={false}
              priority
            />
          </Box>

          <Box
            as="button"
            role="button"
            aria-label="Toggle menu"
            display={{
              base: "flex",
              xl: "none",
            }}
            onClick={onOpen}
          >
            <Icon
              as={Bars3Icon}
              width={{
                base: "20px",
                lg: "32px",
              }}
              height={{
                base: "20px",
                lg: "32px",
              }}
            />
          </Box>

          <HStack
            spacing="40px"
            display={{
              base: "none",
              xl: "block",
            }}
          >
            <For each={links} getKey={(_, index) => index}>
              {({ path, label }) => (
                <Button
                  as={Link}
                  href={path}
                  size="lg"
                  variant="link.default"
                  rounded="none"
                  borderBottom="1px solid"
                  borderColor="transparent"
                  paddingY="12px"
                  color="rgba(13, 48, 80, 0.8)"
                  _hover={{
                    color: "#0D3050",
                    filter: "drop-shadow(0px 1px 2px rgba(16, 24, 40, 0.05))",
                    borderColor: "primary.light",
                  }}
                  sx={{
                    ...(router.pathname === path && {
                      color: "#0D3050",
                      borderColor: "primary.light",
                      filter: "drop-shadow(0px 1px 2px rgba(16, 24, 40, 0.05))",
                    }),
                  }}
                >
                  {label}
                </Button>
              )}
            </For>

            <Calendly size="lg" color="#0D3050" bgColor={"primary.light"}>
              Schedule a call
            </Calendly>
          </HStack>
        </Flex>
      </Box>

      <MobileNavbar open={isOpen} onClose={onClose} />
    </>
  );
}

type MobileNavProps = {
  open: boolean;
  onClose: () => void;
};

function MobileNavbar({ open, onClose }: MobileNavProps) {
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const shouldOpen = !!open && breakpoint !== "xl";

  return (
    <Drawer
      isOpen={shouldOpen}
      onClose={onClose}
      size={{
        base: "xs",
        lg: "md",
      }}
    >
      <DrawerOverlay />
      <DrawerContent padding={0}>
        <DrawerBody padding={0}>
          <HStack justifyContent="space-between" padding="16px">
            <Text
              fontSize={"18px"}
              lineHeight={"24px"}
              fontWeight="600"
              color="#0D3050"
              fontFamily="'Inter', sans-serif"
            >
              Menu
            </Text>

            <Flex as="button" onClick={onClose}>
              <Icon
                as={XMarkIcon}
                width={{
                  base: "20px",
                  lg: "24px",
                }}
                height={{
                  base: "20px",
                  lg: "24px",
                }}
              />
            </Flex>
          </HStack>

          <List>
            <For each={links}>
              {({ path, label, icon }) => (
                <ListItem
                  borderTop="1px solid"
                  borderColor="rgba(240,237,236,0.4)"
                  transition="background-color 250ms ease-in-out, color 250ms ease-in-out"
                  color="rgba(13, 48, 80, 0.8)"
                  borderLeft="1px solid"
                  borderLeftColor="transparent"
                  sx={{
                    ...(router.pathname === path && {
                      bgColor: "#F0EDEC",
                      color: "#0D3050",
                      borderLeftColor: "#F9D209",
                    }),
                  }}
                >
                  <HStack as={Link} href={path} display="flex" padding="16px" spacing="16px">
                    <Icon as={icon} width="20px" height="20px" stroke="rgba(13,48,80,0.8)" />

                    <Text
                      variant="18/18"
                      fontWeight="medium"
                      color="rgba(13,48,80,0.8)"
                      fontFamily="'Inter', sans-serif"
                    >
                      {label}
                    </Text>
                  </HStack>
                </ListItem>
              )}
            </For>
            <Flex px="16px" pt="16px" w="full">
              <Calendly size="lg" w="full">
                Schedule a call
              </Calendly>
            </Flex>
          </List>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

const links = [
  /*
  {
    path: "/pricing",
    label: "Pricing",
    icon: ShieldDollarIcon,
  },
  */
  {
    path: "/team",
    label: "Team",
    icon: UsersIcon,
  },

  {
    path: "/blog",
    label: "Blog",
    icon: PencilIcon,
  },
  {
    path: "/events",
    label: "Events",
    icon: MegaphoneIcon,
  },
  {
    path: "/resources",
    label: "Resources",
    icon: ShieldDollarIcon,
  },
];

function revealOnScrollUp(elem: HTMLElement) {
  let prevScrollpos = window.pageYOffset;

  window.addEventListener("scroll", () => {
    const currentScrollPos = window.pageYOffset;
    elem.style.top = prevScrollpos > currentScrollPos ? "0" : "-100%";
    prevScrollpos = currentScrollPos;
  });
}
